/**
 * Platform-wide CMS, SEO, feature flags, taxonomy, and abuse settings (Firebase RTDB).
 * Path: admin/platformConfig
 */

import { ref, set, get, onValue } from "firebase/database";
import { db } from "../auth/firebase";
import { debug } from "../utils/logger";

const CONFIG_PATH = "admin/platformConfig";
/** Public read mirror of `featureFlags` for anonymous clients (see `database.rules.json`). */
export const PUBLIC_FEATURE_FLAGS_PATH = "publicFeatureFlags";
/** Public read mirror of CMS, SEO, taxonomy, and abuse keywords. */
export const PUBLIC_SITE_CONFIG_PATH = "publicSiteConfig";

function asStringList(value, maxItems, maxLen) {
  const source = Array.isArray(value) ? value : [];
  const out = [];
  for (const item of source) {
    const text = String(item || "").trim().slice(0, maxLen);
    if (!text || out.includes(text)) continue;
    out.push(text);
    if (out.length >= maxItems) break;
  }
  return out;
}

export function toPublicSiteConfig(config = {}, extras = {}) {
  const cms = config.cms || {};
  const seo = config.seo || {};
  return {
    cms: {
      heroTitle: String(cms.heroTitle || "").slice(0, 140),
      heroSubtitle: String(cms.heroSubtitle || "").slice(0, 280),
      aboutTeaser: String(cms.aboutTeaser || "").slice(0, 600),
      policiesNote: String(cms.policiesNote || "").slice(0, 400),
    },
    seo: {
      defaultTitle: String(seo.defaultTitle || "").slice(0, 140),
      defaultDescription: String(seo.defaultDescription || "").slice(0, 300),
      allowIndexing: seo.allowIndexing !== false,
      canonicalBase: String(seo.canonicalBase || "").slice(0, 200),
      sitemapUrl: String(seo.sitemapUrl || "").slice(0, 300),
    },
    categories: asStringList(config.categories, 40, 60),
    tags: asStringList(config.tags, 80, 40),
    blockedKeywords: asStringList(config.abuse?.blockedKeywords, 100, 40),
    maintenanceMode: extras.maintenanceMode === true,
    pushNotificationsEnabled: extras.pushNotificationsEnabled !== false,
    updatedAt: new Date().toISOString(),
  };
}

function isPermissionDenied(e) {
  const code = e?.code;
  const msg = String(e?.message || "");
  return code === "permission-denied" || /permission denied/i.test(msg);
}

export const DEFAULT_PLATFORM_CONFIG = Object.freeze({
  cms: {
    heroTitle: "",
    heroSubtitle: "",
    aboutTeaser: "",
    policiesNote: "",
  },
  seo: {
    defaultTitle: "",
    defaultDescription: "",
    allowIndexing: true,
    canonicalBase: "",
    sitemapUrl: "",
  },
  featureFlags: {
    commentsGloballyEnabled: true,
    newReaderSplitDashboard: true,
  },
  categories: [],
  tags: [],
  abuse: {
    blockedKeywords: [],
  },
  rolePolicy: {
    /** Free-text notes for auditors; role matrix is enforced in app + Firebase rules */
    notes: "",
  },
});

function deepMerge(base, patch) {
  if (patch == null || typeof patch !== "object" || Array.isArray(patch)) return patch;
  const out = { ...base };
  Object.keys(patch).forEach((k) => {
    const pv = patch[k];
    const bv = base?.[k];
    if (pv != null && typeof pv === "object" && !Array.isArray(pv) && bv && typeof bv === "object" && !Array.isArray(bv)) {
      out[k] = deepMerge(bv, pv);
    } else {
      out[k] = pv;
    }
  });
  return out;
}

/**
 * @returns {Promise<typeof DEFAULT_PLATFORM_CONFIG>}
 */
export async function getPlatformConfig() {
  try {
    const snap = await get(ref(db, CONFIG_PATH));
    if (!snap.exists()) {
      return { ...DEFAULT_PLATFORM_CONFIG };
    }
    return deepMerge({ ...DEFAULT_PLATFORM_CONFIG }, snap.val());
  } catch (e) {
    if (isPermissionDenied(e)) {
      debug("[platformConfig] using defaults (path not readable for this session)");
      return { ...DEFAULT_PLATFORM_CONFIG };
    }
    console.error("[platformConfig] get failed:", e);
    return { ...DEFAULT_PLATFORM_CONFIG };
  }
}

/**
 * @param {Partial<typeof DEFAULT_PLATFORM_CONFIG>} patch
 */
export async function savePlatformConfig(patch) {
  const current = await getPlatformConfig();
  const next = deepMerge(current, patch);
  next.updatedAt = new Date().toISOString();
  await set(ref(db, CONFIG_PATH), next);
  if (next.featureFlags && typeof next.featureFlags === "object") {
    await set(ref(db, PUBLIC_FEATURE_FLAGS_PATH), next.featureFlags);
  }
  const existingPublic = await getPublicSiteConfig();
  await set(
    ref(db, PUBLIC_SITE_CONFIG_PATH),
    toPublicSiteConfig(next, {
      maintenanceMode: existingPublic.maintenanceMode,
      pushNotificationsEnabled: existingPublic.pushNotificationsEnabled,
    })
  );
  return next;
}

export async function getPublicSiteConfig() {
  try {
    const snap = await get(ref(db, PUBLIC_SITE_CONFIG_PATH));
    if (!snap.exists()) return toPublicSiteConfig(DEFAULT_PLATFORM_CONFIG);
    return { ...toPublicSiteConfig(DEFAULT_PLATFORM_CONFIG), ...snap.val() };
  } catch (e) {
    if (isPermissionDenied(e)) return toPublicSiteConfig(DEFAULT_PLATFORM_CONFIG);
    console.error("[publicSiteConfig] get failed:", e);
    return toPublicSiteConfig(DEFAULT_PLATFORM_CONFIG);
  }
}

export async function patchPublicSiteConfig(patch) {
  const current = await getPublicSiteConfig();
  const next = { ...current, ...patch, updatedAt: new Date().toISOString() };
  await set(ref(db, PUBLIC_SITE_CONFIG_PATH), next);
  return next;
}

/**
 * @param {(config: typeof DEFAULT_PLATFORM_CONFIG) => void} callback
 * @returns {() => void}
 */
export function subscribePlatformConfig(callback) {
  const r = ref(db, CONFIG_PATH);
  const unsub = onValue(r, (snap) => {
    if (!snap.exists()) {
      callback({ ...DEFAULT_PLATFORM_CONFIG });
      return;
    }
    callback(deepMerge({ ...DEFAULT_PLATFORM_CONFIG }, snap.val()));
  });
  return () => unsub();
}

/**
 * Real-time public feature flags (readable without auth). Merged with defaults.
 * @param {(flags: typeof DEFAULT_PLATFORM_CONFIG.featureFlags) => void} callback
 * @returns {() => void}
 */
export function subscribePublicFeatureFlags(callback) {
  const r = ref(db, PUBLIC_FEATURE_FLAGS_PATH);
  const unsub = onValue(r, (snap) => {
    const base = { ...DEFAULT_PLATFORM_CONFIG.featureFlags };
    if (!snap.exists()) {
      callback(base);
      return;
    }
    const v = snap.val();
    callback(typeof v === "object" && v && !Array.isArray(v) ? { ...base, ...v } : base);
  });
  return () => unsub();
}
